/**
 * `NumericInput` — a text input that holds a number without fighting the
 * person typing into it.
 *
 * Primitive layer (`.tondocs/skills/react-primitive-design.md` §3 three-layer,
 * §4 controlled). Stateless with respect to the VALUE — the parent still owns
 * that — but it owns the DRAFT, which is the point.
 *
 * # Why not `<Input type="number" value={n} onChange={e => set(Number(...))}>`
 *
 * That shape has three defects, all of which shipped in the listing forms
 * before this primitive existed (scar 2026-08-03):
 *
 *  1. **The draft is derived, so partial entries are erased mid-keystroke.**
 *     `Number("6.")` is `6`; re-rendering `String(6)` deletes the decimal point
 *     the moment it is typed, and no fractional value can ever be entered.
 *  2. **A lossy store rewrites what you typed.** The parcel fields converted
 *     inches → integer mm → inches on every keystroke, so typing `6` produced
 *     `5.98` on screen. Any unit conversion or rounding in the parent has this
 *     effect.
 *  3. **`type="number"` reports invalid intermediate input as `""`.** Typing
 *     `-`, `1e`, or a comma hands the handler an empty string, which silently
 *     clears the field. It also mutates on mouse-wheel scroll over the input.
 *
 * `NumericInput` keeps the typed characters in state and re-syncs from `value`
 * only when `value` changed for a reason OTHER than this field's own emit —
 * a form load, a reset, a parent that clamped. The test is
 * `codec.parse(draft) !== value`: while typing, the draft already parses to
 * the value the parent holds, so nothing is overwritten.
 *
 * # Usage
 *
 * ```tsx
 * const codec = useMemo(() => decimalCodec(2), [])
 * <NumericInput value={price} onValueChange={setPrice} codec={codec} />
 * ```
 *
 * `codec` maps draft ⇄ value and may convert units — see
 * `lib/shipping/parcel-locale-codec.ts`, where the draft is inches and the
 * value is millimetres. Memoise it if it is built from props; a fresh object
 * each render is harmless (it is never a dependency) but pointless.
 *
 * # Deliberate non-features
 *
 * - **No min/max clamping.** Clamping while typing makes a field impossible to
 *   fill: `min=10` would rewrite the `1` of `12` to `10`. Range belongs to
 *   validation, which the caller renders after the fact (see the parcel form's
 *   `violations`).
 * - **No formatting while focused** — thousands separators and unit suffixes
 *   move the caret. Normalisation happens on blur only.
 *
 * @see lib/forms/numeric-draft.ts (sanitise / parse / codecs — the domain half)
 * @see .tondocs/ui/MOBILE_INPUTS.md (16px minimum — inherited from `Input`)
 */
"use client";

import * as React from "react";

import { Input, type InputProps } from "@/components/ui/input";
import {
  sanitizeNumericDraft,
  type NumericFieldCodec,
} from "@/lib/forms/numeric-draft";

export interface NumericInputProps
  extends Omit<
    InputProps,
    "value" | "defaultValue" | "onChange" | "type" | "inputMode" | "pattern"
  > {
  /**
   * The stored number, in whatever unit `codec` encodes to. `undefined` is a
   * cleared field — distinct from `0`, which is a number the user typed.
   */
  value: number | undefined;
  /**
   * Called on every accepted keystroke with the parsed value, or `undefined`
   * when the field is empty. Fires with the SAME value on keystrokes that do
   * not change it (typing `.` after `6`), which is what lets the draft keep
   * its trailing separator.
   */
  onValueChange: (next: number | undefined) => void;
  /** Draft ⇄ value mapping. See `lib/forms/numeric-draft.ts`. */
  codec: NumericFieldCodec;
}

export const NumericInput = React.forwardRef<
  HTMLInputElement,
  NumericInputProps
>(function NumericInput(
  { value, onValueChange, codec, onBlur, ...rest },
  ref,
) {
  const [draft, setDraft] = React.useState(() =>
    value === undefined ? "" : codec.format(value),
  );

  // Re-sync from `value` during render rather than in an effect — this is the
  // "adjusting state when a prop changes" pattern from the React docs, and it
  // avoids the extra commit (and visible flash) an effect would cause.
  // `react-state-design §2`: an effect here would be deriving state, not
  // syncing with the outside world.
  const [lastSyncedValue, setLastSyncedValue] = React.useState(value);
  if (value !== lastSyncedValue) {
    setLastSyncedValue(value);
    // Only overwrite the draft when the incoming value is NOT what the draft
    // already means. Typing `6` → `6.` emits the same value twice; without
    // this guard the second render would rewrite `"6."` back to `"6"` and eat
    // the decimal point.
    if (value !== codec.parse(draft)) {
      setDraft(value === undefined ? "" : codec.format(value));
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = sanitizeNumericDraft(event.target.value, {
      maxDecimals: codec.maxDecimals,
      allowNegative: codec.allowNegative,
    });
    setDraft(next);
    const parsed = codec.parse(next);
    // Record what we are about to emit so the value coming back down does not
    // read as an external change. If the parent stores something else (a
    // clamp, a lossy unit conversion), the render-time check above catches the
    // difference and re-syncs — but only then, and only once.
    setLastSyncedValue(parsed);
    onValueChange(parsed);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    // Settle the draft to the canonical rendering of what is actually stored:
    // `"6."` → `"6"`, `"007"` → `"7"`, and for a lossy store, the value the
    // user will see when they come back. Done on blur so it never interrupts
    // typing.
    const parsed = codec.parse(draft);
    setDraft(parsed === undefined ? "" : codec.format(parsed));
    onBlur?.(event);
  };

  return (
    <Input
      ref={ref}
      type="text"
      inputMode={codec.maxDecimals > 0 ? "decimal" : "numeric"}
      // iOS uses `pattern` to pick the keypad when `inputMode` is ambiguous.
      pattern={codec.maxDecimals > 0 ? "[0-9]*[.,]?[0-9]*" : "[0-9]*"}
      autoComplete="off"
      value={draft}
      onChange={handleChange}
      onBlur={handleBlur}
      {...rest}
    />
  );
});
